import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { queryJobs } from '@/features/jobs/data/jobs';
import {
  buildStageNameFilters,
  buildStagePipelineFilters,
} from '@/features/pipelines/hooks/stagePageFilters';
import { fetchAllQueryPages } from '@/shared/data/fetchAllQueryPages';
import { queryKeys } from '@/shared/hooks';
import {
  buildDateRangeFilters,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type { BuildStatus, JobListItemDto } from '@flowqa/shared';

type UseStageHistoryJobsArgs = {
  isAuthenticated: boolean;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  selectedPipelineIds: string[];
  selectedStageNames: string[];
  selectedStatuses: BuildStatus[];
  selectedTags: string[];
  effectiveStartDay: string | null;
  effectiveEndDay: string | null;
  isLatestMode: boolean;
  hasLatestTimestamp: boolean;
};

type UseStageHistoryJobsResult = {
  jobsByStageId: ReadonlyMap<string, readonly JobListItemDto[]>;
  isFetching: boolean;
  isError: boolean;
};

export const useStageHistoryJobs = ({
  isAuthenticated,
  selectedOrganisationId,
  selectedProjectId,
  selectedPipelineIds,
  selectedStageNames,
  selectedStatuses,
  selectedTags,
  effectiveStartDay,
  effectiveEndDay,
  isLatestMode,
  hasLatestTimestamp,
}: UseStageHistoryJobsArgs): UseStageHistoryJobsResult => {
  const currentStageJobsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'stages/page',
      'jobs',
      selectedOrganisationId ?? null,
      selectedProjectId ?? null,
      [...selectedPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStageNames].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStatuses].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedTags].sort((left, right) => left.localeCompare(right)).join(','),
      effectiveStartDay,
      effectiveEndDay,
    ),
    enabled: isAuthenticated && (!isLatestMode || hasLatestTimestamp),
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryJobs(selectedProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            sorts: [{ field: 'jobName', direction: 'asc' }],
            filters: mergeFilters(
              buildInFilter('status', selectedStatuses),
              buildStagePipelineFilters(selectedPipelineIds),
              buildStageNameFilters(selectedStageNames),
              buildInFilter('tags', selectedTags),
              buildDateRangeFilters(
                'startedAt',
                effectiveStartDay ? dayjs(effectiveStartDay).startOf('day').toISOString() : null,
                effectiveEndDay ? dayjs(effectiveEndDay).endOf('day').toISOString() : null,
              ),
            ),
          }),
      ),
  });

  const jobsByStageId = useMemo(() => {
    const map = new Map<string, JobListItemDto[]>();
    (currentStageJobsQuery.data?.items ?? []).forEach((job) => {
      const stageId = `${job.pipelineId ?? 'null'}:${job.stageName ?? ''}`;
      const items = map.get(stageId) ?? [];
      items.push(job);
      map.set(stageId, items);
    });
    return map;
  }, [currentStageJobsQuery.data?.items]);

  return {
    jobsByStageId,
    isFetching: currentStageJobsQuery.isFetching,
    isError: currentStageJobsQuery.isError,
  };
};
